import { useEffect, useRef } from 'react';
import { MediaControlsDataSetState, MediaControlsStateInterface, MediaControlsHookInterface } from '../contexts/media-controls';
import { videoActionCommands } from '../utils/video-machine';
import type { MediaItem } from '../stores/types/media';

interface UseMediaControlsSyncProps {
	allMediaControls: MediaControlsDataSetState;
	media: MediaItem[];
}

/**
 * Custom hook to synchronize play/pause/mute/unmute/volume/scrub state changes
 * across all media controls. This ensures that when one media control changes state,
 * all other media controls are updated accordingly.
 */
export function useMediaControlsSync({ allMediaControls, media }: UseMediaControlsSyncProps) {
	const allMediaControlsRef = useRef(allMediaControls);
	const prevMediaControlsStateRef = useRef<Record<string, MediaControlsStateInterface>>({});
	const debounceStateRef = useRef<{
		volume: { source: string | null; timeout: NodeJS.Timeout | null };
		play: { source: string | null; timeout: NodeJS.Timeout | null };
		pause: { source: string | null; timeout: NodeJS.Timeout | null };
		mute: { source: string | null; timeout: NodeJS.Timeout | null };
		unmute: { source: string | null; timeout: NodeJS.Timeout | null };
		scrub: { source: string | null; timeout: NodeJS.Timeout | null };
	}>({
		volume: { source: null, timeout: null },
		play: { source: null, timeout: null },
		pause: { source: null, timeout: null },
		mute: { source: null, timeout: null },
		unmute: { source: null, timeout: null },
		scrub: { source: null, timeout: null },
	});

	const {
		PLAY,
		PAUSE,
		MUTE,
		UNMUTE,
		VOLUME,
		SCRUB,
		STOPSCRUBBING,
		END,
		TIMECHANGE
	} = videoActionCommands;

	useEffect(() => {
		allMediaControlsRef.current = allMediaControls;
	}, [allMediaControls]);

	// Watch for play/pause/volume/mute/scrubbing state changes and sync across all media controls
	useEffect(() => {
		const prevStates = prevMediaControlsStateRef.current;

		// Detect which media control just changed its state
		Object.entries(allMediaControls).forEach(([controlKey, mediaControl]) => {
			if (!mediaControl?.state || !mediaControl?.send) return;

			const prevState = prevStates[controlKey];
			const currentState = mediaControl.state;

			// Skip if this is the first time we're seeing this control
			if (!prevState) {
				console.log('[useMediaControlsSync] First time seeing control, skipping:', {
					controlKey,
					mediaControlId: mediaControl.id,
					currentPaused: currentState.paused
				});
				return;
			}

			// Detect if THIS control just paused (was playing, now paused)
			// Skip when pause was caused by entering scrub state
			const justPaused = prevState.paused === false && currentState.paused === true
				&& !currentState.scrubbing;

			// Detect if THIS control just played (was paused, now playing)
			// Skip when play was caused by exiting scrub state (restoring prior play state)
			const justPlayed = prevState.paused === true && currentState.paused === false
				&& !prevState.scrubbing;

			// Debug logging for justPlayed detection
			if (justPlayed) {
				console.log('[useMediaControlsSync] justPlayed detected:', {
					controlKey,
					mediaControlId: mediaControl.id,
					prevPaused: prevState.paused,
					currentPaused: currentState.paused,
					prevElapsed: prevState.elapsed,
					currentElapsed: currentState.elapsed,
					isDraft: controlKey.indexOf('-draft') > -1,
					timestamp: new Date().toISOString()
				});
			}

			// Detect mute state change
			const justMuted = prevState.muted === false && currentState.muted === true;
			const justUnmuted = prevState.muted === true && currentState.muted === false;

			// Detect volume change (and not muted)
			const volumeChanged = prevState.shownVolume !== currentState.shownVolume
				&& currentState.muted === false;

			// Detect when scrubbing stopped (user finished seeking)
			const stoppedScrubbing = prevState.scrubbing === true && currentState.scrubbing === false;

			// if everything but a play/pause happened, if it is NOT a stylepack, ignore it
			if (justMuted || justUnmuted || volumeChanged || stoppedScrubbing) {
				const key = parseInt(mediaControl.id);
				const mediaItem = media.find(x => x.id == key);
				if (mediaItem && !mediaItem.template_type) return;
			}

			const draftOnly = controlKey.indexOf('-draft') > -1;

			if (justPaused) {
				// Don't unpause the video if it completed before the audio
				if (mediaControl.state.video && (mediaControl.state.video as HTMLVideoElement).ended) return;

				Object.entries(allMediaControls).forEach(([otherKey, otherControl]) => {
					if (otherKey == controlKey || // is the same control that initiated, ignore it
						draftOnly && otherKey.indexOf('-draft') < 0
					) {
						return;
					}

					if (otherControl?.send && !otherControl?.state?.paused) {
						otherControl.send(PAUSE, {
							from: 'pause sync via useEffect',
							doFireSync: true
						});
					}
				});
			}

			if (justPlayed) {
				console.log('[useMediaControlsSync] justPlayed block executing - syncing play to other controls');

				const prevDuration = prevState.duration ?? 0;
				const prevElapsed = prevState.elapsed ?? 0;
				const currElapsed = currentState.elapsed ?? 0;
				const wasAtEnd = prevDuration > 0 && prevElapsed >= prevDuration - 0.5;
				const restartedFromEnd = wasAtEnd && currElapsed < 0.5;

				const triggeringMedia = media.find(x => String(x.id) === controlKey);
				const isLooping = !!triggeringMedia?.media_data?.loop;

				Object.entries(allMediaControls).forEach(([otherKey, otherControl]) => {
					if (otherKey == controlKey // is the same control that initiated, ignore it
						|| (draftOnly && otherKey.indexOf('-draft') < 0)
						|| !otherControl?.send
					) {
						console.log('[useMediaControlsSync] Skipping sync for control:', {
							otherKey,
							reason: otherKey === controlKey ? 'same control' : 'draft mismatch'
						});
						return;
					}

					if (otherControl.state?.paused) {
						otherControl.send(PLAY, {
							from: 'play sync via useEffect',
							doFireSync: true,
						});
					} else if (restartedFromEnd && !isLooping) {
						otherControl.send(TIMECHANGE, {
							currentTime: 0,
							from: 'restart sync',
						});
					} else {
						console.log('[useMediaControlsSync] Not sending PLAY to:', {
							otherKey,
							reason: !otherControl?.send ? 'no send method' : 'already playing'
						});
					}
				});
			}

			if (justMuted) {
				Object.entries(allMediaControls).forEach(([otherKey, otherControl]) => {
					if (otherKey == controlKey || // is the same control that initiated, ignore it
						draftOnly && otherKey.indexOf('-draft') < 0
					) {
						return;
					}

					if (otherControl?.send && !otherControl?.state?.muted) {
						otherControl.send(MUTE, {
							from: 'mute sync via useEffect',
							doFireSync: true
						});
					}
				});
			}

			if (justUnmuted) {
				Object.entries(allMediaControls).forEach(([otherKey, otherControl]) => {
					if (otherKey == controlKey || // is the same control that initiated, ignore it
						draftOnly && otherKey.indexOf('-draft') < 0
					) {
						return;
					}

					if (otherControl?.send && otherControl?.state?.muted) {
						otherControl.send(UNMUTE, {
							from: 'unmute sync via useEffect',
							doFireSync: true
						});
					}
				});
			}

			if (volumeChanged) {
				// Prevent feedback loop: if this volume change was caused by a sync from another control, ignore it
				if (debounceStateRef.current.volume.source && debounceStateRef.current.volume.source !== controlKey) {
					return;
				}

				if (debounceStateRef.current.volume.timeout) {
					clearTimeout(debounceStateRef.current.volume.timeout);
				}

				// Mark this control as the source of volume change
				debounceStateRef.current.volume.source = controlKey;

				// Debounce
				debounceStateRef.current.volume.timeout = setTimeout(() => {
					// Update volume for all OTHER media controls
					Object.entries(allMediaControls).forEach(([otherKey, otherControl]) => {
						if (otherKey == controlKey || // is the same control that initiated, ignore it
							draftOnly && otherKey.indexOf('-draft') < 0
						) {
							return;
						}

						if (otherControl?.send) {
							otherControl.send(VOLUME, {
								shownVolume: currentState.shownVolume,
								volume: currentState.volume,
								from: 'volume sync via useEffect',
								doFireSync: true
							});
						}
					});

					setTimeout(() => {
						debounceStateRef.current.volume.source = null;
					}, 200);
				}, 150);
			}

			if (stoppedScrubbing) {
				// Prevent feedback loop
				if (debounceStateRef.current.scrub.source && debounceStateRef.current.scrub.source !== controlKey) {
					return;
				}

				if (debounceStateRef.current.scrub.timeout) {
					clearTimeout(debounceStateRef.current.scrub.timeout);
				}

				// Mark this control as the source
				debounceStateRef.current.scrub.source = controlKey;

				// Debounce
				debounceStateRef.current.scrub.timeout = setTimeout(() => {
					// Sync the final scrub position to all OTHER media controls
					Object.entries(allMediaControls).forEach(([otherKey, otherControl]) => {
						if (otherKey == controlKey || // is the same control that initiated, ignore it
							draftOnly && otherKey.indexOf('-draft') < 0
						) {
							return;
						}

						if (otherControl?.send) {
							otherControl.send(SCRUB, {
								currentTime: currentState.elapsed,
								from: 'scrub sync via useEffect',
								doFireSync: true
							});
							otherControl.send(STOPSCRUBBING, {
								from: 'scrub sync via useEffect',
								doFireSync: true
							});
						}
					});

					setTimeout(() => {
						debounceStateRef.current.scrub.source = null;
					}, 100);
				}, 100);
			}
		});

		// Store current states for next comparison
		const newStates: Record<string, MediaControlsStateInterface> = {};
		Object.entries(allMediaControls).forEach(([key, control]) => {
			if (control?.state) {
				newStates[key] = { ...control.state };
			}
		});
		prevMediaControlsStateRef.current = newStates;
	}, [allMediaControls, PLAY, PAUSE, MUTE, UNMUTE, VOLUME, SCRUB, STOPSCRUBBING, END, media]);
}
